import { type ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { CardIcon } from '@/components/ui/card'
import { BarChart3 } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ListCard } from './ListCard'
import { ListCardHeader } from './ListCardHeader'
import { DateRangeSelect } from './DateRangeSelect'
import { SiteStatsTable } from './SiteStatsTable'
import { AdUnitStatsTable } from './AdUnitStatsTable'
import { ArticleStatsTable } from './ArticleStatsTable'
import { TrafficStatsTable } from './TrafficStatsTable'
import { LocationStatsTable } from './LocationStatsTable'
import {
  TargetingTotalsTable,
  type AdUnitTargeting,
  type BreakdownTableProps,
} from './TargetingTotalsTable'
import { usePersistedState } from '../hooks/usePersistedState'
import { useStatsRange } from '../hooks/useStatsRange'
import type { StatsQuery } from '@/services/stats/statsApi'
import type { DateRange } from '@/services/stats/statsRange'

/**
 * One tab per breakdown the stats API offers. Each has its own endpoint, so
 * each tab is its own table component fetching its own data — inactive tabs
 * are unmounted and never fetch.
 *
 * To add a breakdown: write a table component taking {@link BreakdownTableProps}
 * and add it here.
 */
const BREAKDOWN_TABS: {
  key: string
  labelKey: string
  Table: ComponentType<BreakdownTableProps>
}[] = [
  { key: 'targeting', labelKey: 'stats.targetingTotals', Table: TargetingTotalsTable },
  { key: 'sites', labelKey: 'sites.title', Table: SiteStatsTable },
  { key: 'adUnits', labelKey: 'adUnits.title', Table: AdUnitStatsTable },
  { key: 'articles', labelKey: 'stats.topArticles', Table: ArticleStatsTable },
  { key: 'traffic', labelKey: 'stats.traffic', Table: TrafficStatsTable },
  { key: 'locations', labelKey: 'stats.locations', Table: LocationStatsTable },
]

export interface DeliveryBreakdownCardProps {
  /** Which stats to break down — e.g. `{ lineItemId }`. */
  query: Partial<StatsQuery>
  /** Namespaces the remembered tab. */
  storageKey: string
  defaultRange?: DateRange
  /**
   * Period to show. Pass it to follow another panel's period — the card then
   * leaves the picker to whoever owns the range. Leave it out and the card
   * follows the app-wide stats period.
   */
  range?: DateRange
  /** Lets the ad unit tab include or exclude ad units from targeting. */
  adUnitTargeting?: AdUnitTargeting
}

/** How a line item delivered, broken down along one dimension per tab. */
export function DeliveryBreakdownCard({
  query,
  storageKey,
  defaultRange = '24h',
  range: controlledRange,
  adUnitTargeting,
}: DeliveryBreakdownCardProps) {
  const { t } = useTranslation()
  const [ownRange, setOwnRange] = useStatsRange(defaultRange)
  const range = controlledRange ?? ownRange
  const [savedTab, setSavedTab] = usePersistedState<string>(
    `breakdown.tab.${storageKey}`,
    BREAKDOWN_TABS[0].key
  )

  // A remembered tab that no longer exists falls back to the first one.
  const activeTab = BREAKDOWN_TABS.some((tab) => tab.key === savedTab)
    ? savedTab
    : BREAKDOWN_TABS[0].key

  return (
    <ListCard tone="insight">
      <ListCardHeader
        bulkActionsAfterFilters
        title={
          <>
            <CardIcon icon={BarChart3} />
            {t('stats.breakdown')}
          </>
        }
        // The picker belongs to whoever owns the range: with a controlled range
        // the panel above shows it, and a second copy would only be noise.
        controls={
          controlledRange ? undefined : <DateRangeSelect value={range} onChange={setOwnRange} />
        }
      />
      <Tabs value={activeTab} onValueChange={setSavedTab}>
        <div className="px-(--card-spacing)">
          <TabsList className="h-auto! flex-wrap gap-y-1">
            {BREAKDOWN_TABS.map((tab) => (
              <TabsTrigger key={tab.key} value={tab.key} className="flex-none">
                {t(tab.labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {BREAKDOWN_TABS.map(({ key, Table }) => (
          <TabsContent key={key} value={key}>
            <Table query={query} range={range} adUnitTargeting={adUnitTargeting} />
          </TabsContent>
        ))}
      </Tabs>
    </ListCard>
  )
}
